import React, { useCallback, useEffect, useState, startTransition } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Footer from "../../../../Components/Footer/Footer";
import QuizeResults from "./QuizeResults";
import KnowledgeTestQuizPageHeader from "./KnowledgeTestQuizPageHeader";
import { GetLicenseTypes } from "../../../../Actions/CategoryA";
import { fetchKnowledgeQuizAttemptDetails } from "./knowledgeQuizApi";
import { mapHistoryDetailsToQuizResults } from "./quizResultsNormalizer";
import {
    quizAttemptPath,
    quizHomePath,
    quizLicenseTypePath,
    quizLicenseTypeSelectPath,
    parseKnowledgeHistoryAttemptId,
} from "./knowledgeQuizRoutes";

function QuizPageShell({ header, children }) {
    return (
        <div className="mt-20">
            {header}
            {children}
        </div>
    );
}


function QuizResultsDetails() {
    const { pathname } = useLocation();
    const attemptIdentifier = parseKnowledgeHistoryAttemptId(pathname);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { LicenseTypes } = useSelector((store) => store.LicenseTypeReducer);
    const [results, setResults] = useState([]);
    const [serverScore, setServerScore] = useState(null);
    const [licenseLabel, setLicenseLabel] = useState("");
    const [questionCount, setQuestionCount] = useState(null);
    const [quizProfileCode, setQuizProfileCode] = useState("");
    const [licenseTypeId, setLicenseTypeId] = useState(null);
    const [subLicenseTypeId, setSubLicenseTypeId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!LicenseTypes?.length) {
            dispatch(GetLicenseTypes());
        }
    }, [dispatch, LicenseTypes?.length]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!attemptIdentifier) {
                setError("Missing quiz attempt id.");
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                setError(null);
                const response = await fetchKnowledgeQuizAttemptDetails(
                    attemptIdentifier
                );

                if (cancelled) return;

                if (!response?.success || !response?.data) {
                    setError(response?.error || "Failed to load quiz details.");
                    return;
                }

                const mapped = mapHistoryDetailsToQuizResults(response.data);
                setResults(mapped.results);
                setServerScore(mapped.serverScore);
                setLicenseLabel(mapped.licenseLabel || "");
                setQuestionCount(mapped.questionCount);
                setQuizProfileCode(mapped.quizProfileCode || "");
                setLicenseTypeId(mapped.licenseTypeId ?? null);
                setSubLicenseTypeId(mapped.subLicenseTypeId ?? null);
            } catch (err) {
                if (!cancelled) {
                    setError(
                        err?.message ||
                            "Unable to load quiz details. Please try again."
                    );
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [attemptIdentifier]);

    const resolveLicenseName = useCallback(() => {
        const fromList = LicenseTypes?.find(
            (item) => String(item.LicenseTypeId) === String(licenseTypeId)
        );
        return fromList?.LicenseTypeName ?? "";
    }, [LicenseTypes, licenseTypeId]);

    const subLicenseSlugFromLabel = useCallback(() => {
        return String(licenseLabel || "")
            .replace(/^Class\s+/i, "")
            .trim()
            .toLowerCase();
    }, [licenseLabel]);

    const handleRetakeQuiz = useCallback(() => {
        const licenseTypeName = resolveLicenseName();
        const canStartDirect = !!licenseTypeId && questionCount != null;

        startTransition(() => {
            if (!canStartDirect || !licenseTypeName) {
                navigate(
                    licenseTypeName
                        ? quizLicenseTypeSelectPath(licenseTypeName)
                        : quizHomePath()
                );
                return;
            }

            navigate(
                quizAttemptPath({
                    licenseTypeName,
                    subLicenseTypeName: subLicenseTypeId
                        ? subLicenseSlugFromLabel()
                        : undefined,
                    questionCount,
                })
            );
        });
    }, [
        navigate,
        questionCount,
        licenseTypeId,
        subLicenseTypeId,
        resolveLicenseName,
        subLicenseSlugFromLabel,
    ]);

    const onChangeLicenseCategory = useCallback(() => {
        const licenseTypeName = resolveLicenseName();

        startTransition(() => {
            if (!licenseTypeId || !licenseTypeName) {
                navigate(quizHomePath());
                return;
            }

            navigate(
                subLicenseTypeId
                    ? quizLicenseTypePath(licenseTypeName)
                    : quizHomePath()
            );
        });
    }, [navigate, licenseTypeId, subLicenseTypeId, resolveLicenseName]);

    const quizHeader = (
        <KnowledgeTestQuizPageHeader
            licenseLabel={licenseLabel}
            questionCount={questionCount}
        />
    );

    if (isLoading) {
        return (
            <QuizPageShell header={quizHeader}>
                <div className="flex justify-center px-4 py-16">
                    <img
                        src={require("../../../CourseCategory/sublicenseAllCourses/Assets/filters-loading.gif")}
                        alt="Loading"
                    />
                </div>
            </QuizPageShell>
        );
    }

    if (error) {
        return (
            <QuizPageShell header={quizHeader}>
                <p className="px-4 text-center text-red-600">{error}</p>
            </QuizPageShell>
        );
    }

    return (
        <QuizPageShell header={quizHeader}>
            <QuizeResults
                results={results}
                serverScore={serverScore}
                handleRetakeQuiz={handleRetakeQuiz}
                onChangeLicenseCategory={onChangeLicenseCategory}
            />
            <Footer />
        </QuizPageShell>
    );
}

export default QuizResultsDetails;
